/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { BADGE_TYPE_STRONG, Badge, ICON_CHEVRON_RIGHT, ICON_FOLDER_SPECIAL, SdsIcon } from '@squiz/sds';
import { getCapabilityPath } from 'apps/console-core/src/Utilities/GetCapabilityPath/GetCapabilityPath';
import React, { KeyboardEvent, MouseEvent, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { COMPONENT_SERVICE_STRINGS } from '../../../../Constants/Strings.constants';
import { getComponentSetPath } from '../../../../Utilities/GetComponentSetPath/GetComponentSetPath';
import { COMPONENT_SETS_DATA_IDS } from '../../ComponentSets.constants';
import { ComponentSetItemProps } from './ComponentSetItem.props';

/**
 * Component set list item.
 */
export const ComponentSetItem = (props: ComponentSetItemProps): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { set } = props;

  const labels = {
    components: t(COMPONENT_SERVICE_STRINGS.components),
  };

  // Set props
  const { components, description, displayName, webPath } = set;
  const setCount = String(Object.keys(components).length);

  // Component set details path handling
  const currentScreenPath = getCapabilityPath(`/component-sets`);
  const componentSetDetailsPath = `${currentScreenPath}${getComponentSetPath(`/`, webPath)}`;

  const navigateToComponentSetDetails = useCallback(
    (componentSetDetailsPath: string): void => {
      navigate({ pathname: componentSetDetailsPath });
    },
    [navigate],
  );

  const goToComponentSetDetailsMouseHandler = (event: MouseEvent<HTMLButtonElement>): void => {
    navigateToComponentSetDetails(componentSetDetailsPath);
  };

  const goToComponentSetDetailsKeyboardHandler = (event: KeyboardEvent<HTMLButtonElement>): void => {
    // Keys which should navigate user to the component set details screen.
    const navigateKeys = ['Enter', 'Space'];
    if (navigateKeys.includes(event.code)) {
      navigateToComponentSetDetails(componentSetDetailsPath);
    }
  };

  return (
    <div className="component-set__item">
      <button
        className="component-set__link"
        onClick={goToComponentSetDetailsMouseHandler}
        onKeyDown={goToComponentSetDetailsKeyboardHandler}
      >
        <div className="component-set__card">
          {/* Set Icon (Default for now) */}
          <div className="component-set__icon" data-id={COMPONENT_SETS_DATA_IDS.setIcon}>
            <SdsIcon svg={ICON_FOLDER_SPECIAL} />
          </div>

          <div className="component-set__body">
            {/* Right Arrow Chevron */}
            <div className="component-set__right-arrow">
              <SdsIcon className="component-set__right-arrow" svg={ICON_CHEVRON_RIGHT} />
            </div>

            {/* Set Title & Description */}
            <div className="component-set__header">
              <span className="component-set__title" data-id={COMPONENT_SETS_DATA_IDS.setTitle}>
                {displayName}
              </span>
              {description && (
                <span className="component-set__subtitle" data-id={COMPONENT_SETS_DATA_IDS.setDescription}>
                  {description}
                </span>
              )}
            </div>

            {/* Contents (Count of Components) */}
            <div className="component-set__content">
              <Badge left={setCount} type={BADGE_TYPE_STRONG} />
              <span>{labels.components}</span>
            </div>
          </div>
        </div>
      </button>
    </div>
  );
};
